"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  FiArrowLeft, FiCheckCircle, FiClock, FiUser, FiCalendar,
  FiFileText, FiAward, FiLink, FiMessageSquare, FiAlertCircle,
  FiXCircle, FiRefreshCw, FiChevronRight, FiUserCheck,
  FiBookOpen, FiTrendingUp, FiShield, FiExternalLink, FiInfo, FiDownload,
  FiCopy, FiRotateCcw,
} from "react-icons/fi"
import { FaSpinner, FaCheck, FaBan, FaRupeeSign, FaUpload, FaTimes } from "react-icons/fa"
import api from "../../../libs/axios"
import toast from "react-hot-toast"
import { useAuth } from "../../../provider/auth.provider"
import { SelectWithSearch2, type SelectOption } from "../../../utils/select-with-search"
import { AddCertificationOffcanvas } from "../certifications/add"
import { uploadFilesWithPresignedUrls } from "../../../libs/presigned-upload"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

interface UserRef { id: number; first_name: string; last_name: string; email?: string; phone_number?: string }
interface TrainingType { id: number; training_name: string; validity_months?: number }
interface TrainingInstitute { id: number; institute_name: string; institute_short_name?: string }
interface InstituteCenter { id: number; center_name: string; contact_person?: string; contact_phone?: string; contact_email?: string; address?: string }
interface TrainingCertification {
  id: number; certificate_number: string; training_date: string; expiry_date: string
  training_amount?: number; deduction_amount?: number; deduction_status: string
  deducted_at?: string; deduction_remarks?: string; is_verified: boolean; verified_at?: string
  verification_remarks?: string; linked_to_permanent_user?: boolean; employee_code?: string
  card_front_url?: string; card_back_url?: string
  creator?: UserRef; verifier?: UserRef
  certifications?: Array<{ id: number; user?: UserRef }>
}
interface PaymentRecord {
  id: number
  training_payment?: {
    id: number; pi_number?: string; pi_total_amount?: number
    payment_status: string; payment_type: string; paid_at?: string
    per_person_amount?: number; creator?: UserRef; payer?: UserRef
  }
}
interface WorkflowLog {
  id: number; action_type: string; from_status?: string; to_status?: string
  performed_at: string; remarks?: string; metadata?: any
  performer?: UserRef
}
interface TrainingDocument { id: number; document_type: string; document_url: string; created_at?: string }
interface FullTrainingRequest {
  id: number; request_id: string; candidate_name: string; candidate_phone: string
  candidate_alternate_phone?: string; candidate_location: string; candidate_designation: string
  remarks?: string
  association_type: string; status: string; user_id?: number
  deduction_applicable: boolean; deduction_period?: string; deduction_remarks?: string
  salary_offered?: number; planned_training_date?: string; planned_location?: string; reporting_time?: string
  hr_verified_at?: string; training_verified_at?: string; scheduled_at?: string; completed_at?: string
  rejected_at?: string; rejection_remarks?: string; created_at?: string
  training_type?: TrainingType; training_institute?: TrainingInstitute; institute_center?: InstituteCenter
  project?: { id: number; project_name: string }
  vendor?: { id: number; vendor_name?: string; vendor_full_name?: string }
  creator?: UserRef; hr_verifier?: UserRef; training_verifier?: UserRef
  scheduler?: UserRef; completer?: UserRef; rejecter?: UserRef; linked_user?: UserRef
  documents?: TrainingDocument[]; certification?: TrainingCertification
  payment_records?: PaymentRecord[]; workflow_logs?: WorkflowLog[]
}

interface DetailRow {
  label: string
  value?: string | null
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  hr_verified: "bg-blue-100 text-blue-800 border-blue-200",
  training_verified: "bg-indigo-100 text-indigo-800 border-indigo-200",
  scheduled: "bg-purple-100 text-purple-800 border-purple-200",
  paid: "bg-teal-100 text-teal-800 border-teal-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Request Raised",
  hr_verified: "HR Verified",
  training_verified: "Training Verified",
  scheduled: "Training Scheduled",
  paid: "Cost Updated",
  completed: "Training Completed",
  rejected: "Rejected",
  cancelled: "Cancelled",
}

const ACTION_LABELS: Record<string, string> = {
  request_created: "Request Raised",
  hr_verified: "HR Verified",
  training_verified: "Training Verified",
  scheduled: "Training Scheduled",
  cost_updated: "Cost Updated",
  completed: "Training Completed",
  certification_done: "Certification Done",
  amount_deducted: "Amount Deducted",
  user_linked: "User Linked",
  rejected: "Rejected",
  cancelled: "Cancelled",
  comment_added: "Comment Added",
  document_uploaded: "Document Uploaded",
  payment_created: "Payment Created",
  payment_marked_paid: "Payment Marked Paid",
}

function formatDate(d?: string) {
  if (!d) return "-"
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

function formatDateOnly(d?: string) {
  if (!d) return "-"
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

// Workflow steps definition
const WORKFLOW_STEPS = [
  { key: "pending", label: "Request Raised", icon: FiFileText, description: "Training request submitted" },
  { key: "hr_verified", label: "HR Verification", icon: FiUserCheck, description: "Verified by HR" },
  { key: "training_verified", label: "Training Verification", icon: FiShield, description: "Training team verified" },
  { key: "scheduled", label: "Training Scheduled", icon: FiCalendar, description: "Date & institute confirmed" },
  { key: "completed", label: "Training Completed", icon: FiCheckCircle, description: "Training done" },
  { key: "certification_done", label: "Certification Done", icon: FiAward, description: "Certificate issued" },
  { key: "user_linked", label: "Linked to Permanent User", icon: FiLink, description: "Linked to system user" },
]

const STATUS_ORDER = ["pending", "hr_verified", "training_verified", "scheduled", "completed"] as const
const STATUS_STAGE_MAP: Record<string, typeof STATUS_ORDER[number]> = {
  pending: "pending",
  hr_verified: "hr_verified",
  training_verified: "training_verified",
  scheduled: "scheduled",
  paid: "scheduled",
  completed: "completed",
}

function formatCurrency(value?: string | number | null) {
  if (value === undefined || value === null || value === "") return "-"
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return String(value)
  return `Rs. ${numericValue.toLocaleString("en-IN")}`
}

function getTimelineStatus(request: FullTrainingRequest): typeof STATUS_ORDER[number] {
  const mappedStatus = STATUS_STAGE_MAP[request.status]
  if (mappedStatus) return mappedStatus

  const logs = [...(request.workflow_logs || [])].reverse()
  for (const log of logs) {
    const mappedToStatus = log.to_status ? STATUS_STAGE_MAP[log.to_status] : undefined
    if (mappedToStatus) return mappedToStatus

    const mappedFromStatus = log.from_status ? STATUS_STAGE_MAP[log.from_status] : undefined
    if (mappedFromStatus) return mappedFromStatus
  }

  return "pending"
}

function getStepStatus(stepKey: string, requestStatus: string, request: FullTrainingRequest): "completed" | "current" | "pending" {
  if (stepKey === "certification_done") {
    if (request.certification) return "completed"
    return "pending"
  }
  if (stepKey === "user_linked") {
    if (Array.isArray(request.certification?.certifications) && request.certification!.certifications!.length > 0) return "completed"
    if (request.user_id && request.certification) return "completed"
    // Only actionable once training is completed and certification exists
    if (STATUS_STAGE_MAP[request.status] === "completed" && request.certification) return "current"
    return "pending"
  }
  const stepIndex = STATUS_ORDER.indexOf(stepKey as (typeof STATUS_ORDER)[number])
  const currentIndex = STATUS_ORDER.indexOf(getTimelineStatus(request))
  if (stepIndex < 0 || currentIndex < 0) return "pending"
  if (requestStatus === "rejected" || requestStatus === "cancelled") {
    if (stepIndex <= currentIndex) return "completed"
    return "pending"
  }
  if (stepIndex < currentIndex) return "completed"
  if (stepIndex === currentIndex) return "current"
  return "pending"
}

export default function TrainingRequestView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const canHrVerifyNewUser = hasPermission("TrainingRequest", "hr_verify_new_user")
  const canHrVerifyExistingUser = hasPermission("TrainingRequest", "hr_verify_existing_user")
  const canDuplicateRequest = hasPermission("TrainingRequest", "duplicate")
  const canTrainingVerify = hasPermission("TrainingRequest", "training_verify")
  const canSchedule = hasPermission("TrainingRequest", "schedule")
  const canComplete = hasPermission("TrainingRequest", "complete")
  const canAddCertification = hasPermission("TrainingCertification", "create")
  const canLinkUser = hasPermission("TrainingRequest", "link_user")
  const canUpdateDeduction = hasPermission("TrainingCertification", "update_deduction")
  const canRevert = hasPermission("TrainingRequest", "revert")

  const [request, setRequest] = useState<FullTrainingRequest | null>(null)
  const canHrVerifyRequest = Boolean(request?.user_id) ? canHrVerifyExistingUser : canHrVerifyNewUser
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  // Action states
  const [showHrAction, setShowHrAction] = useState(false)
  const [showTrainingVerify, setShowTrainingVerify] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showComplete, setShowComplete] = useState(false)
  const [showAddCert, setShowAddCert] = useState(false)
  const [showLinkUser, setShowLinkUser] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [trainingTypes, setTrainingTypes] = useState<any[]>([])
  const [certInstitutes, setCertInstitutes] = useState<any[]>([])

  // Form states
  const [salaryOffered, setSalaryOffered] = useState("")
  const [altPhone, setAltPhone] = useState("")
  const [hrProjectId, setHrProjectId] = useState<SelectOption | null>(null)
  const [hrProjects, setHrProjects] = useState<SelectOption[]>([])
  const [rejectionRemarks, setRejectionRemarks] = useState("")
  const [verifyRemarks, setVerifyRemarks] = useState("")
  const [plannedDate, setPlannedDate] = useState("")
  const [plannedLocation, setPlannedLocation] = useState("")
  const [reportingTime, setReportingTime] = useState("")
  const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null)
  const [institutes, setInstitutes] = useState<SelectOption[]>([])
  const [completeRemarks, setCompleteRemarks] = useState("")
  const [completeAction, setCompleteAction] = useState<"complete" | "reschedule" | "cancel_attended" | "cancel_no_attended">("complete")
  const [linkUserId, setLinkUserId] = useState("")
  const [comment, setComment] = useState("")
  const [users, setUsers] = useState<SelectOption[]>([])
  // Task 4: Training verify reject
  const [trainingVerifyAction, setTrainingVerifyAction] = useState<"verify" | "reject">("verify")
  const [verifyRejectionRemarks, setVerifyRejectionRemarks] = useState("")
  // Safety documents upload during training verify
  const [safetyDocFiles, setSafetyDocFiles] = useState<File[]>([])
  // HR documents upload during HR verify
  const [hrDocFiles, setHrDocFiles] = useState<File[]>([])
  // Permanent ID card upload during link user
  const [permanentIdCardFile, setPermanentIdCardFile] = useState<File | null>(null)
  const [permanentIdCardBackFile, setPermanentIdCardBackFile] = useState<File | null>(null)
  // Deduction update offcanvas from request view
  const [showDeductionUpdate, setShowDeductionUpdate] = useState(false)
  // Revert workflow one step
  const [showRevert, setShowRevert] = useState(false)
  const [revertRemarks, setRevertRemarks] = useState("")
  // Reschedule fields (used when completeAction === 'reschedule')
  const [reschedPlannedDate, setReschedPlannedDate] = useState("")
  const [reschedPlannedLocation, setReschedPlannedLocation] = useState("")
  const [reschedReportingTime, setReschedReportingTime] = useState("")
  const [reschedInstitute, setReschedInstitute] = useState<SelectOption | null>(null)
  const [reschedCenter, setReschedCenter] = useState<SelectOption | null>(null)
  const [reschedInstituteCenters, setReschedInstituteCenters] = useState<SelectOption[]>([])
  const [selectedCenter, setSelectedCenter] = useState<SelectOption | null>(null)
  const [instituteCenters, setInstituteCenters] = useState<SelectOption[]>([])
  const [deductionAction, setDeductionAction] = useState<"deducted" | "waived" | "pending" | "payment_received">("deducted")
  const [deductionAmount, setDeductionAmount] = useState("")
  const [deductionDate, setDeductionDate] = useState("")
  const [deductionRemarks, setDeductionRemarks] = useState("")
  const [paymentReceivedAmount, setPaymentReceivedAmount] = useState("")
  const [paymentReceivedDate, setPaymentReceivedDate] = useState("")
  const [deductionDocFiles, setDeductionDocFiles] = useState<File[]>([])
  const [deductionDocType, setDeductionDocType] = useState("deduction_slip")
  const [deductionDocDragging, setDeductionDocDragging] = useState(false)
  const [certDocuments, setCertDocuments] = useState<Array<{ id: number; document_type: string; document_name: string; document_url: string; created_at?: string }>>([])
  const deductionDocInputRef = useRef<HTMLInputElement>(null)
  const addDeductionDocFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList)
    if (!files.length) return
    setDeductionDocFiles(prev => [...prev, ...files])
  }
  const [activeTab, setActiveTab] = useState<"workflow" | "overview" | "certification" | "payments">("workflow")
  const [stepDetailsOpen, setStepDetailsOpen] = useState(false)
  const [selectedStepKey, setSelectedStepKey] = useState("")

  const fetchRequest = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      const res = await api.get(`/training/requests/${id}/workflow`)
      const loaded: FullTrainingRequest | null = res.data.data.training_request || null

      if (loaded && !loaded.certification) {
        try {
          const certListRes = await api.get("/training/certifications/list", {
            params: { training_request_id: loaded.id, page: 1, limit: 1 },
          })
          const list = certListRes.data?.data?.training_certifications || certListRes.data?.data?.certifications || []
          const found = Array.isArray(list) ? list[0] : null
          if (found?.id) {
            const certRes = await api.get(`/training/certifications/${found.id}`)
            const detailed = certRes.data?.data?.training_certification || certRes.data?.data?.certification
            if (detailed) loaded.certification = detailed
          }
        } catch { }
      }

      if (loaded?.certification?.id) {
        try {
          const docsRes = await api.get(`/training/certifications/${loaded.certification.id}/documents`)
          setCertDocuments(docsRes.data?.data?.documents || [])
        } catch { setCertDocuments([]) }
      } else {
        setCertDocuments([])
      }

      setRequest(loaded)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load training request")
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchRequest()
  }, [fetchRequest])

  useEffect(() => {
    api.get("/training/institutes/active").then(res => {
      const list = res.data.data.institutes || []
      setInstitutes(list.map((i: any) => ({ id: i.id, value: i.id, label: i.institute_name })))
      setCertInstitutes(list)
    }).catch(() => { })
    api.get("/projects/list", { params: { status: 'ACTIVE', limit: 100 } }).then(res => {
      setHrProjects((res.data.data.projects || []).map((p: any) => ({ id: p.id, value: p.id, label: p.project_name || `Project #${p.id}` })))
    }).catch(() => { })
    api.get("/training/courses/list", { params: { limit: 100, is_active: 'true' } }).then(res => {
      setTrainingTypes(res.data.data.training_types || [])
    }).catch(() => { })
  }, [])

  useEffect(() => {
    if (!selectedInstitute) { setInstituteCenters([]); setSelectedCenter(null); return }
    api.get(`/training/institutes/${selectedInstitute.value}/centers`)
      .then(res => {
        const list = res.data?.data?.centers || []
        setInstituteCenters(list.map((c: any) => ({ id: c.id, value: c.id, label: c.center_name })))
      })
      .catch(() => setInstituteCenters([]))
    setSelectedCenter(null)
  }, [selectedInstitute])

  useEffect(() => {
    if (!reschedInstitute) { setReschedInstituteCenters([]); setReschedCenter(null); return }
    api.get(`/training/institutes/${reschedInstitute.value}/centers`)
      .then(res => {
        const list = res.data?.data?.centers || []
        setReschedInstituteCenters(list.map((c: any) => ({ id: c.id, value: c.id, label: c.center_name })))
      })
      .catch(() => setReschedInstituteCenters([]))
    setReschedCenter(null)
  }, [reschedInstitute])

  const handleHrDecision = async (approved: boolean) => {
    if (!request) return
    setActionLoading(true)
    try {
      if (!hrProjectId) { toast.error("Project is required"); setActionLoading(false); return }
      const data: any = { is_approved: approved, project_id: Number(hrProjectId.value) }
      if (approved) {
        if (salaryOffered) data.salary_offered = Number(salaryOffered)
        if (altPhone) data.candidate_alternate_phone = altPhone
      } else {
        if (!rejectionRemarks.trim()) { toast.error("Rejection remarks required"); setActionLoading(false); return }
        data.rejection_remarks = rejectionRemarks.trim()
      }
      await api.patch(`/training/requests/${request.id}/hr-verify`, data)
      // Upload HR documents if provided
      if (approved && hrDocFiles.length > 0) {
        const urls = await uploadFilesWithPresignedUrls(hrDocFiles)
        for (const url of urls) {
          await api.post(`/training/requests/${request.id}/documents`, { document_type: "hr_document", document_url: url })
        }
      }
      toast.success(approved ? "Request HR verified" : "Request rejected")
      setShowHrAction(false)
      setSalaryOffered(""); setAltPhone(""); setHrProjectId(null); setRejectionRemarks(""); setHrDocFiles([])
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const handleTrainingVerify = async () => {
    if (!request) return
    setActionLoading(true)
    try {
      if (trainingVerifyAction === "reject") {
        if (!verifyRejectionRemarks.trim()) { toast.error("Rejection remarks required"); setActionLoading(false); return }
        await api.patch(`/training/requests/${request.id}/training-verify`, {
          is_approved: false,
          rejection_remarks: verifyRejectionRemarks.trim(),
        })
        toast.success("Training request rejected")
      } else {
        // Upload safety documents if any
        let uploadedSafetyUrls: string[] = []
        if (safetyDocFiles.length > 0) {
          uploadedSafetyUrls = await uploadFilesWithPresignedUrls(safetyDocFiles)
        }
        await api.patch(`/training/requests/${request.id}/training-verify`, {
          remarks: verifyRemarks.trim() || undefined,
          is_approved: true,
        })
        // Post each safety doc
        for (const url of uploadedSafetyUrls) {
          await api.post(`/training/requests/${request.id}/documents`, {
            document_type: "safety_document",
            document_url: url,
          })
        }
        toast.success("Training verified")
      }
      setShowTrainingVerify(false)
      setVerifyRemarks(""); setVerifyRejectionRemarks(""); setTrainingVerifyAction("verify"); setSafetyDocFiles([])
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const handleSchedule = async () => {
    if (!request || !plannedDate) { toast.error("Training date is required"); return }
    setActionLoading(true)
    try {
      const data: any = { planned_training_date: plannedDate }
      if (plannedLocation) data.planned_location = plannedLocation
      if (reportingTime) data.reporting_time = reportingTime
      if (selectedInstitute) data.training_institute_id = Number(selectedInstitute.value)
      if (selectedCenter) data.institute_center_id = Number(selectedCenter.value)
      await api.patch(`/training/requests/${request.id}/schedule`, data)
      toast.success("Training scheduled")
      setShowSchedule(false); setPlannedDate(""); setPlannedLocation(""); setReportingTime(""); setSelectedInstitute(null); setSelectedCenter(null); setInstituteCenters([])
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const handleComplete = async () => {
    if (!request) return
    setActionLoading(true)
    try {
      const msgs: Record<string, string> = {
        complete: "Training marked as completed",
        reschedule: "Training rescheduled successfully",
        cancel_attended: "Training cancelled (attended)",
        cancel_no_attended: "Training cancelled (not attended)",
      }
      const payload: any = { remarks: completeRemarks.trim() || undefined, action: completeAction }
      if (completeAction === "reschedule" && reschedPlannedDate) {
        payload.planned_training_date = reschedPlannedDate
        if (reschedPlannedLocation) payload.planned_location = reschedPlannedLocation
        if (reschedReportingTime) payload.reporting_time = reschedReportingTime
        if (reschedInstitute) {
          payload.training_institute_id = Number(reschedInstitute.value)
          payload.institute_center_id = reschedCenter ? Number(reschedCenter.value) : null
        }
      }
      await api.patch(`/training/requests/${request.id}/complete`, payload)
      toast.success(msgs[completeAction] || "Done")
      setShowComplete(false)
      setCompleteRemarks(""); setCompleteAction("complete")
      setReschedPlannedDate(""); setReschedPlannedLocation(""); setReschedReportingTime(""); setReschedInstitute(null); setReschedCenter(null); setReschedInstituteCenters([])
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const handleRevert = async () => {
    if (!request) return
    setActionLoading(true)
    try {
      await api.patch(`/training/requests/${request.id}/revert`, { remarks: revertRemarks.trim() || undefined })
      toast.success("Workflow reverted one step")
      setShowRevert(false); setRevertRemarks("")
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to revert workflow")
    } finally {
      setActionLoading(false)
    }
  }

  const [downloadingDocs, setDownloadingDocs] = useState(false)
  const handleDownloadAllDocs = async () => {
    if (!request?.documents?.length) return
    setDownloadingDocs(true)
    try {
      const res = await api.get(`/training/requests/${request.id}/documents/zip`, {
        responseType: "blob",
      })
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }))
      const a = document.createElement("a")
      a.href = url
      a.download = `${request.request_id}_docs.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error("Failed to download documents")
    } finally {
      setDownloadingDocs(false)
    }
  }

  const handleDeductionUpdate = async () => {
    if (!request?.certification) return
    setActionLoading(true)
    try {
      const data: any = { deduction_status: deductionAction }
      if (deductionAction === "deducted") {
        data.deduction_amount = Number(deductionAmount) || 0
        if (deductionDate) data.deduction_date = deductionDate
      } else if ((deductionAction === "waived" || deductionAction === "pending") && deductionAmount !== "") {
        // The amount still to be booked - kept so it stays visible until the deduction is booked.
        data.deduction_amount = Number(deductionAmount) || 0
      }
      if (deductionAction === "payment_received") {
        if (paymentReceivedAmount) data.payment_received_amount = Number(paymentReceivedAmount)
        if (paymentReceivedDate) data.payment_received_date = paymentReceivedDate
      }
      if (deductionRemarks.trim()) data.deduction_remarks = deductionRemarks.trim()
      await api.patch(`/training/certifications/${request.certification.id}/deduction`, data)
      for (const file of deductionDocFiles) {
        const urls = await uploadFilesWithPresignedUrls([file])
        await api.post(`/training/certifications/${request.certification.id}/documents`, {
          document_url: urls[0],
          document_type: deductionDocType,
          document_name: file.name,
        })
      }
      toast.success("Deduction updated")
      setShowDeductionUpdate(false); setDeductionAmount(""); setDeductionDate(""); setDeductionRemarks(""); setDeductionAction("deducted")
      setPaymentReceivedAmount(""); setPaymentReceivedDate(""); setDeductionDocFiles([]); setDeductionDocType("deduction_slip"); setDeductionDocDragging(false)
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update deduction")
    } finally {
      setActionLoading(false)
    }
  }

  const handleLinkUser = async () => {
    if (!request || !linkUserId) { toast.error("Select a user to link"); return }
    setActionLoading(true)
    try {
      let permanent_id_card_url: string | undefined
      let permanent_id_card_back_url: string | undefined
      if (permanentIdCardFile) {
        const urls = await uploadFilesWithPresignedUrls([permanentIdCardFile])
        permanent_id_card_url = urls[0]
      }
      if (permanentIdCardBackFile) {
        const urls = await uploadFilesWithPresignedUrls([permanentIdCardBackFile])
        permanent_id_card_back_url = urls[0]
      }
      await api.post(`/training/requests/${request.id}/link-user`, {
        user_id: Number(linkUserId),
        permanent_id_card_url,
        permanent_id_card_back_url,
      })
      toast.success("User linked successfully")
      setShowLinkUser(false); setLinkUserId(""); setPermanentIdCardFile(null); setPermanentIdCardBackFile(null)
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const handleAddCertification = async (data: any) => {
    setActionLoading(true)
    try {
      await api.post("/training/certifications/create", data)
      toast.success("Certification added successfully")
      setShowAddCert(false)
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add certification")
    } finally {
      setActionLoading(false)
    }
  }

  const handleComment = async () => {
    if (!request || !comment.trim()) { toast.error("Comment is required"); return }
    setActionLoading(true)
    try {
      await api.post(`/training/requests/${request.id}/comment`, { remarks: comment.trim() })
      toast.success("Comment added")
      setShowComment(false); setComment("")
      fetchRequest()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed")
    } finally {
      setActionLoading(false)
    }
  }

  const searchUsers = async (q: string) => {
    if (!q || q.trim().length < 3) {
      setUsers([])
      return
    }
    try {
      const res = await api.get('/users/list', { params: { search: q.trim(), limit: 20, src: 'all' } })
      const list = res.data.data?.users || res.data.data?.team || []
      setUsers(list.map((u: any) => ({ id: u.id, value: u.id, label: `${u.first_name} ${u.last_name}${u.employee_code ? ` (${u.employee_code})` : ''} — ${u.phone_number || u.email || ""}` })))
    } catch { }
  }

  const debouncedSearchUsers = useDebouncedCallback(searchUsers, 400)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <FaSpinner className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    )
  }

  if (!request) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-gray-500">
        <FiAlertCircle className="w-12 h-12 mb-2" />
        <p>Training request not found.</p>
      </div>
    )
  }

  const statusColor = STATUS_COLORS[request.status] || "bg-gray-100 text-gray-800 border-gray-200"
  const isTerminal = ["rejected", "cancelled"].includes(request.status)

  // Step key → workflow log action_type mapping
  const STEP_LOG_ACTIONS: Record<string, string[]> = {
    pending: ["request_created"],
    hr_verified: ["hr_verified", "rejected"],
    training_verified: ["training_verified"],
    scheduled: ["scheduled"],
    completed: ["completed"],
    certification_done: ["certification_done"],
    user_linked: ["user_linked"],
  }

  // Step key → document types mapping
  const STEP_DOC_TYPES: Record<string, string[]> = {
    pending: ["requestor_doc"],
    hr_verified: ["hr_document"],
    training_verified: ["safety_document"],
    certification_done: [],
    user_linked: [],
  }

  const getStepLogs = (stepKey: string) =>
    (request.workflow_logs || []).filter((log) => {
      if (stepKey === "pending") return log.action_type === "request_created"
      if (stepKey === "hr_verified") {
        return log.action_type === "hr_verified" || (log.action_type === "rejected" && log.metadata?.step === "hr_verified")
      }
      if (stepKey === "training_verified") {
        return log.action_type === "training_verified" || (log.action_type === "rejected" && log.metadata?.step === "training_verified")
      }
      if (stepKey === "scheduled") return log.action_type === "scheduled"
      if (stepKey === "completed") return log.action_type === "completed" || log.action_type === "cancelled"
      if (stepKey === "certification_done") return log.action_type === "certification_done"
      if (stepKey === "user_linked") return log.action_type === "user_linked"
      return false
    })

  const getStepDocs = (stepKey: string) => {
    const types = STEP_DOC_TYPES[stepKey] || []
    return (request.documents || []).filter(d => types.includes(d.document_type))
  }

  const visibleWorkflowSteps = WORKFLOW_STEPS.filter((step) => step.key !== "user_linked" || !request.user_id)
  const selectedStep = visibleWorkflowSteps.find(s => s.key === selectedStepKey)
  const stepLogs = selectedStepKey ? getStepLogs(selectedStepKey) : []
  const stepDocs = selectedStepKey ? getStepDocs(selectedStepKey) : []
  const latestStepLog = stepLogs.length > 0 ? stepLogs[stepLogs.length - 1] : undefined
  const latestStepMetadata = latestStepLog?.metadata || {}
  const linkedUserDocuments = selectedStepKey === "user_linked"
    ? [
        { label: "Permanent ID Card Front", url: latestStepMetadata.permanent_id_card_url as string | undefined },
        { label: "Permanent ID Card Back", url: latestStepMetadata.permanent_id_card_back_url as string | undefined },
      ].filter((doc): doc is { label: string; url: string } => Boolean(doc.url))
    : []

  const buildDetailRows = (rows: DetailRow[]) =>
    rows.filter(({ value }) => value !== undefined && value !== null && String(value).trim() !== "" && String(value).trim() !== "-")

  const stepDetailRows = (() => {
    switch (selectedStepKey) {
      case "pending":
        return buildDetailRows([
          { label: "Request ID", value: request.request_id },
          { label: "Candidate Name", value: request.candidate_name },
          { label: "Phone", value: request.candidate_phone },
          { label: "Alternate Phone", value: request.candidate_alternate_phone },
          { label: "Location", value: request.candidate_location },
          { label: "Designation", value: request.candidate_designation },
          { label: "Association Type", value: request.association_type?.toLowerCase() === 'onrole' ? 'On-Role' : 'Off-Role' },
          { label: "Training Type", value: request.training_type?.training_name },
          { label: "Vendor", value: request.vendor?.vendor_full_name || request.vendor?.vendor_name },
          { label: "Existing User", value: request.linked_user ? `${request.linked_user.first_name} ${request.linked_user.last_name}` : request.user_id ? `User #${request.user_id}` : undefined },
          { label: "Deduction Applicable", value: request.deduction_applicable ? "Yes" : "No" },
          { label: "Deduction Period", value: request.deduction_period?.replace(/_/g, " ") },
          { label: "Deduction Remarks", value: request.deduction_remarks },
          { label: "Request Remarks", value: request.remarks },
          { label: "Created At", value: formatDate(request.created_at) },
          { label: "Created By", value: request.creator ? `${request.creator.first_name} ${request.creator.last_name}` : undefined },
        ])
      case "hr_verified":
        return buildDetailRows([
          { label: "Decision", value: latestStepLog?.action_type === "rejected" ? "Rejected" : request.hr_verified_at ? "Approved" : undefined },
          { label: "Salary Offered", value: formatCurrency(latestStepMetadata.salary_offered ?? request.salary_offered) },
          { label: "Alternate Phone", value: latestStepMetadata.candidate_alternate_phone ?? request.candidate_alternate_phone },
          { label: "Project", value: latestStepMetadata.project_name ?? request.project?.project_name ?? (latestStepMetadata.project_id ? `Project #${latestStepMetadata.project_id}` : undefined) },
          { label: "Verified At", value: formatDate(request.hr_verified_at) },
          { label: "Verified By", value: request.hr_verifier ? `${request.hr_verifier.first_name} ${request.hr_verifier.last_name}` : undefined },
          { label: "Rejection Remarks", value: latestStepLog?.action_type === "rejected" ? (latestStepLog?.remarks || request.rejection_remarks) : undefined },
        ])
      case "training_verified":
        return buildDetailRows([
          { label: "Decision", value: latestStepLog?.action_type === "rejected" ? "Rejected" : request.training_verified_at ? "Verified" : undefined },
          { label: "Remarks", value: latestStepLog?.remarks },
          { label: "Verified At", value: formatDate(request.training_verified_at) },
          { label: "Verified By", value: request.training_verifier ? `${request.training_verifier.first_name} ${request.training_verifier.last_name}` : undefined },
        ])
      case "scheduled":
        return buildDetailRows([
          { label: "Rescheduled", value: latestStepMetadata.is_reschedule ? "Yes" : undefined },
          { label: "Training Date", value: formatDateOnly((latestStepMetadata.planned_training_date as string | undefined) || request.planned_training_date) },
          { label: "Training Location", value: latestStepMetadata.planned_location ?? request.planned_location },
          { label: "Reporting Time", value: latestStepMetadata.reporting_time ?? request.reporting_time },
          { label: "Institute", value: latestStepMetadata.training_institute_name ?? request.training_institute?.institute_name ?? (latestStepMetadata.training_institute_id ? `Institute #${latestStepMetadata.training_institute_id}` : undefined) },
          { label: "Center", value: latestStepMetadata.institute_center_name ?? request.institute_center?.center_name ?? (latestStepMetadata.institute_center_id ? `Center #${latestStepMetadata.institute_center_id}` : undefined) },
          { label: "Scheduled At", value: formatDate(request.scheduled_at) },
          { label: "Scheduled By", value: request.scheduler ? `${request.scheduler.first_name} ${request.scheduler.last_name}` : undefined },
          { label: "Remarks", value: latestStepLog?.remarks },
        ])
      case "completed":
        return buildDetailRows([
          { label: "Action", value: latestStepMetadata.action ? String(latestStepMetadata.action).replace(/_/g, " ") : request.status === "completed" ? "complete" : undefined },
          { label: "Cancel Type", value: latestStepMetadata.cancel_type ? String(latestStepMetadata.cancel_type).replace(/_/g, " ") : undefined },
          { label: "Completed At", value: formatDate(request.completed_at) },
          { label: "Completed By", value: request.completer ? `${request.completer.first_name} ${request.completer.last_name}` : undefined },
          { label: "Remarks", value: latestStepLog?.remarks },
        ])
      case "certification_done":
        return buildDetailRows([
          { label: "Certificate Number", value: request.certification?.certificate_number },
          { label: "Training Type", value: request.training_type?.training_name },
          { label: "Institute", value: request.training_institute?.institute_name },
          { label: "Training Date", value: formatDateOnly(request.certification?.training_date) },
          { label: "Expiry Date", value: formatDateOnly(request.certification?.expiry_date) },
          { label: "Training Amount", value: formatCurrency(request.certification?.training_amount) },
          { label: "Deduction Status", value: request.certification?.deduction_status?.replace(/_/g, " ") },
          { label: "Deduction Amount", value: formatCurrency(request.certification?.deduction_amount) },
          { label: "Deduction Remarks", value: request.certification?.deduction_remarks },
          { label: "Verified", value: request.certification ? (request.certification.is_verified ? "Yes" : "No") : undefined },
          { label: "Verification Remarks", value: request.certification?.verification_remarks },
          { label: "Employee Code", value: request.certification?.employee_code },
          { label: "Linked To Permanent User", value: request.certification?.linked_to_permanent_user ? "Yes" : undefined },
          { label: "Added By", value: request.certification?.creator ? `${request.certification.creator.first_name} ${request.certification.creator.last_name}` : undefined },
        ])
      case "user_linked":
        return buildDetailRows([
          { label: "Existing User Request", value: request.user_id ? "Yes" : "No" },
          { label: "User", value: request.linked_user ? `${request.linked_user.first_name} ${request.linked_user.last_name}` : undefined },
          { label: "Link Source", value: latestStepMetadata.source ? String(latestStepMetadata.source).replace(/_/g, " ") : undefined },
          { label: "Permanent ID Card Front", value: latestStepMetadata.permanent_id_card_url ? "Uploaded" : undefined },
          { label: "Permanent ID Card Back", value: latestStepMetadata.permanent_id_card_back_url ? "Uploaded" : undefined },
          { label: "Link Remarks", value: latestStepLog?.remarks },
        ])
      default:
        return []
    }
  })()

  const TABS = [
    { id: "workflow", label: "Workflow", icon: FiTrendingUp },
    { id: "overview", label: "Overview", icon: FiInfo },
    { id: "certification", label: "Certification", icon: FiAward },
    { id: "payments", label: "Payments", icon: FaRupeeSign },
  ]

  return (
    <>
      <div className="mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-100">
              <FiArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{request.candidate_name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm text-gray-500 font-mono">{request.request_id}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${statusColor}`}>
                  {STATUS_LABELS[request.status] || request.status}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchRequest} className="p-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-600">
              <FiRefreshCw className="w-4 h-4" />
            </button>
            {canDuplicateRequest && (
              <button
                onClick={() => navigate("/training/requests", { state: { duplicateRequest: request } })}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-indigo-200 rounded hover:bg-indigo-50 text-indigo-700"
              >
                <FiCopy className="w-4 h-4" /> Duplicate
              </button>
            )}
            {request && (request.documents?.length ?? 0) > 0 && (
              <button
                onClick={handleDownloadAllDocs}
                disabled={downloadingDocs}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-60"
                title="Download all documents as ZIP"
              >
                <FiDownload className="w-4 h-4" /> {downloadingDocs ? "Downloading..." : `All Docs (${request.documents?.length})`}
              </button>
            )}
            <button
              onClick={() => setShowComment(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
            >
              <FiMessageSquare className="w-4 h-4" /> Comment
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded border border-gray-200">
          <nav className="flex border-b border-gray-200 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                    ? "border-blue-800 text-blue-800"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "workflow" && (
          <div className="space-y-4">
            {/* Workflow Timeline */}
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <FiTrendingUp className="w-5 h-5 text-blue-800" /> Workflow Progress
                </h3>
                {canRevert && !request.certification && request.status !== "pending" && (
                  <button
                    onClick={() => { setRevertRemarks(""); setShowRevert(true) }}
                    className="text-xs px-2.5 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-50 flex items-center gap-1"
                    title="Move the workflow back one step"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" /> Revert one step
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {visibleWorkflowSteps.map((step, idx) => {
                  const status = getStepStatus(step.key, request.status, request)
                  const isLast = idx === visibleWorkflowSteps.length - 1
                  const Icon = step.icon as any

                  return (
                    <div key={step.key} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          onClick={() => { setSelectedStepKey(step.key); setStepDetailsOpen(true) }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 cursor-pointer transition-transform hover:scale-110 ${status === "completed"
                            ? "bg-green-500 border-green-500 text-white"
                            : status === "current"
                            ? "bg-blue-800 border-blue-800 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                          }`}
                        >
                          {status === "completed" ? <FiCheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                        </div>
                        {!isLast && <div className={`w-0.5 flex-1 min-h-4 mt-1 ${status === "completed" ? "bg-green-300" : "bg-gray-200"}`} />}
                      </div>
                      <div className="pb-4 flex-1">
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => { setSelectedStepKey(step.key); setStepDetailsOpen(true) }}
                            className={`text-sm font-medium text-left hover:underline ${status === "completed" ? "text-green-700" : status === "current" ? "text-blue-900" : "text-gray-400"}`}
                          >
                            {step.label}
                          </button>
                          {/* Action Buttons */}
                          {status === "current" && !isTerminal && (
                            <div className="ml-2">
                              {step.key === "pending" && request.status === "pending" && canHrVerifyRequest && (
                                <button onClick={() => setShowHrAction(true)} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">HR Verify</button>
                              )}
                              {step.key === "hr_verified" && request.status === "hr_verified" && canTrainingVerify && (
                                <button onClick={() => setShowTrainingVerify(true)} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">Submit</button>
                              )}
                              {step.key === "training_verified" && request.status === "training_verified" && canSchedule && (
                                <button onClick={() => setShowSchedule(true)} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">Schedule</button>
                              )}
                              {step.key === "scheduled" && (request.status === "scheduled" || request.status === "paid") && canComplete && (
                                <button onClick={() => setShowComplete(true)} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">Mark Completed</button>
                              )}
                              {step.key === "completed" && request.status === "completed" && !request.certification && canAddCertification && (
                                <button onClick={() => setShowAddCert(true)} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">Add Certification</button>
                              )}
                              {step.key === "user_linked" && !request.user_id && request.status === "completed" && request.certification && canLinkUser && (
                                <button onClick={() => { setUsers([]); setLinkUserId(""); setShowLinkUser(true) }} className="text-xs px-2.5 py-1 bg-blue-800 text-white rounded hover:bg-blue-900">Link User</button>
                              )}
                            </div>
                          )}
                          {step.key === "completed" && request.certification && request.deduction_applicable && canUpdateDeduction && (() => {
                            const ds = request.certification?.deduction_status
                            const isLocked = ds === "deducted" || ds === "payment_received"
                            return (
                              <div className="ml-2 flex-shrink-0">
                                <button
                                  onClick={() => { if (isLocked) return; setDeductionAction((ds as any) || "pending"); setDeductionAmount(request.certification?.deduction_amount?.toString() || ""); setShowDeductionUpdate(true) }}
                                  disabled={isLocked}
                                  title={isLocked ? "Deduction is locked (Deduction Booked or Payment Received)" : undefined}
                                  className={`text-xs px-2.5 py-1 rounded ${isLocked ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-800 text-white hover:bg-blue-900'}`}
                                >Update Deduction</button>
                              </div>
                            )
                          })()}
                        </div>
                        <p className={`text-xs mt-0.5 ${status !== "pending" ? "text-gray-500" : "text-gray-400"}`}>{step.description}</p>
                        {step.key === "hr_verified" && request.hr_verifier && (
                          <p className="text-xs text-green-600 mt-0.5">by {request.hr_verifier.first_name} {request.hr_verifier.last_name} · {formatDate(request.hr_verified_at)}</p>
                        )}
                        {step.key === "training_verified" && request.training_verifier && (
                          <p className="text-xs text-indigo-600 mt-0.5">by {request.training_verifier.first_name} {request.training_verifier.last_name} · {formatDate(request.training_verified_at)}</p>
                        )}
                        {step.key === "scheduled" && request.scheduler && (
                          <p className="text-xs text-purple-600 mt-0.5">by {request.scheduler.first_name} {request.scheduler.last_name} · {formatDate(request.scheduled_at)}</p>
                        )}
                        {step.key === "completed" && request.completer && (
                          <p className="text-xs text-green-600 mt-0.5">by {request.completer.first_name} {request.completer.last_name} · {formatDate(request.completed_at)}</p>
                        )}
                        {step.key === "certification_done" && request.certification && (
                          <p className="text-xs text-emerald-600 mt-0.5">
                            Cert: {request.certification.certificate_number}
                            {request.certification.creator && ` · by ${request.certification.creator.first_name} ${request.certification.creator.last_name}`}
                          </p>
                        )}
                        {step.key === "user_linked" && request.certification?.certifications && request.certification.certifications.length > 0 && (
                          <p className="text-xs text-teal-600 mt-0.5">
                            {request.certification.certifications.map(ul => ul.user ? `${ul.user.first_name} ${ul.user.last_name}` : "").filter(Boolean).join(", ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Workflow Audit Log */}
            {request.workflow_logs && request.workflow_logs.length > 0 && (
              <div className="bg-white border border-gray-200 rounded p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiClock className="w-4 h-4 text-gray-500" /> Activity Log
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {[...request.workflow_logs].reverse().map(log => (
                    <div key={log.id} className="flex gap-3 text-sm py-2 border-b border-gray-100 last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">{(log as any)?.metadata?.action === "revert" ? "Workflow Reverted" : (ACTION_LABELS[log.action_type] || log.action_type)}</span>
                          <span className="text-xs text-gray-400">{formatDate(log.performed_at)}</span>
                        </div>
                        {log.from_status && log.to_status && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                            <span className="capitalize">{STATUS_LABELS[log.from_status] || log.from_status}</span>
                            <FiChevronRight className="w-3 h-3" />
                            <span className="capitalize">{STATUS_LABELS[log.to_status] || log.to_status}</span>
                          </div>
                        )}
                        {log.remarks && <p className="text-xs text-gray-600 mt-0.5">{log.remarks}</p>}
                        {log.performer && <p className="text-xs text-gray-400 mt-0.5">by {log.performer.first_name} {log.performer.last_name}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "overview" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Candidate Info */}
            <div className="bg-white border border-gray-200 rounded p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FiUser className="w-4 h-4 text-blue-800" /> Candidate Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">{request.candidate_phone}</span></div>
                {request.candidate_alternate_phone && <div className="flex justify-between"><span className="text-gray-500">Alt. Phone</span><span className="font-medium">{request.candidate_alternate_phone}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{request.candidate_location}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Designation</span><span className="font-medium">{request.candidate_designation}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Association</span><span className="font-medium">{request.association_type?.toLowerCase() === 'onrole' ? 'On-Role' : 'Off-Role'}</span></div>
                {request.salary_offered && <div className="flex justify-between"><span className="text-gray-500">Salary</span><span className="font-medium">₹{Number(request.salary_offered).toLocaleString("en-IN")}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Created</span><span className="font-medium">{formatDateOnly(request.created_at)}</span></div>
                {request.creator && <div className="flex justify-between"><span className="text-gray-500">By</span><span className="font-medium">{request.creator.first_name} {request.creator.last_name}</span></div>}
              </div>
            </div>

            {/* Training Info */}
            <div className="bg-white border border-gray-200 rounded p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FiBookOpen className="w-4 h-4 text-purple-500" /> Training Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{request.training_type?.training_name || "-"}</span></div>
                {request.training_institute && <div className="flex justify-between"><span className="text-gray-500">Institute</span><span className="font-medium">{request.training_institute.institute_name}</span></div>}
                {request.institute_center && <div className="flex justify-between"><span className="text-gray-500">Center</span><span className="font-medium">{request.institute_center.center_name}</span></div>}
                {request.project && <div className="flex justify-between"><span className="text-gray-500">Project</span><span className="font-medium">{request.project.project_name}</span></div>}
                {request.planned_training_date && <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-medium">{formatDateOnly(request.planned_training_date)}</span></div>}
                {request.planned_location && <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-medium">{request.planned_location}</span></div>}
                {request.reporting_time && <div className="flex justify-between"><span className="text-gray-500">Reporting Time</span><span className="font-medium">{request.reporting_time}</span></div>}
              </div>
            </div>

            {/* Deduction Info */}
            {request.deduction_applicable && (
              <div className="bg-orange-50 border border-orange-200 rounded p-4">
                <h3 className="text-sm font-semibold text-orange-800 mb-2 flex items-center gap-2">
                  <FaRupeeSign className="w-3.5 h-3.5" /> Deduction Details
                </h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-orange-600">Period</span><span className="font-medium capitalize">{request.deduction_period?.replace(/_/g, " ") || "-"}</span></div>
                  {request.deduction_remarks && <div><span className="text-orange-600">Remarks: </span><span className="font-medium">{request.deduction_remarks}</span></div>}
                </div>
              </div>
            )}

            {/* Rejection Details */}
            {(request.status === "rejected" || request.status === "cancelled") && (
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                  <FiXCircle className="w-4 h-4" /> {request.status === "cancelled" ? "Cancellation" : "Rejection"} Details
                </h3>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-red-600">{request.status === "cancelled" ? "Cancelled" : "Rejected"} At</span><span>{formatDate(request.rejected_at)}</span></div>
                  {request.rejection_remarks && <div><span className="text-red-600">Reason: </span><span>{request.rejection_remarks}</span></div>}
                </div>
              </div>
            )}

            {/* Documents */}
            {request.documents && request.documents.length > 0 && (
              <div className="bg-white border border-gray-200 rounded p-4 md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <FiFileText className="w-4 h-4 text-gray-500" /> Documents ({request.documents.length})
                  </h3>
                  <button
                    onClick={handleDownloadAllDocs}
                    disabled={downloadingDocs}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-60"
                  >
                    <FiDownload className="w-3 h-3" /> {downloadingDocs ? "Downloading..." : "Download All"}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {request.documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span className="capitalize text-gray-700">{doc.document_type.replace(/_/g, " ")}</span>
                      <a href={doc.document_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-800 hover:underline text-xs">
                        View <FiExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "certification" && (
          <div className="space-y-4">
            {request.certification ? (
              <>
                <div className="bg-white border border-gray-200 rounded p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiAward className="w-4 h-4 text-green-600" /> Certification Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Certificate No.</span><span className="font-mono font-medium">{request.certification.certificate_number}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Training Date</span><span className="font-medium">{formatDateOnly(request.certification.training_date)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Expiry Date</span><span className="font-medium">{formatDateOnly(request.certification.expiry_date)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Verified</span>
                      <span className={`font-medium ${request.certification.is_verified ? "text-green-700" : "text-yellow-700"}`}>
                        {request.certification.is_verified ? "Yes" : "Pending"}
                      </span>
                    </div>
                    {request.certification.training_amount != null && (
                      <div className="flex justify-between"><span className="text-gray-500">Training Amount</span><span className="font-medium">₹{Number(request.certification.training_amount).toLocaleString("en-IN")}</span></div>
                    )}
                    <div className="flex justify-between"><span className="text-gray-500">Deduction Status</span>
                      <span className="font-medium">{({ deducted: "Deduction Booked", payment_received: "Payment Received", waived: "Deduction to be Booked", pending: "Deduction Pending" } as Record<string, string>)[request.certification.deduction_status] || request.certification.deduction_status}</span>
                    </div>
                    {request.certification.deduction_amount != null && (
                      <div className="flex justify-between"><span className="text-gray-500">Deduction Amount</span><span className="font-medium">₹{Number(request.certification.deduction_amount).toLocaleString("en-IN")}</span></div>
                    )}
                    {request.certification.verified_at && (
                      <div className="flex justify-between"><span className="text-gray-500">Verified At</span><span className="font-medium">{formatDate(request.certification.verified_at)}</span></div>
                    )}
                    {request.certification.creator && (
                      <div className="flex justify-between"><span className="text-gray-500">Added By</span><span className="font-medium">{request.certification.creator.first_name} {request.certification.creator.last_name}</span></div>
                    )}
                  </div>
                </div>

                {/* Card Images */}
                {(request.certification.card_front_url || request.certification.card_back_url) && (
                  <div className="bg-white border border-gray-200 rounded p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Certificate Cards</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {request.certification.card_front_url && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Front</p>
                          <a href={request.certification.card_front_url} target="_blank" rel="noreferrer">
                            <img src={request.certification.card_front_url} alt="Card Front" className="w-full rounded border border-gray-200 object-contain max-h-40 bg-gray-50 hover:opacity-90 transition-opacity" />
                          </a>
                        </div>
                      )}
                      {request.certification.card_back_url && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Back</p>
                          <a href={request.certification.card_back_url} target="_blank" rel="noreferrer">
                            <img src={request.certification.card_back_url} alt="Card Back" className="w-full rounded border border-gray-200 object-contain max-h-40 bg-gray-50 hover:opacity-90 transition-opacity" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Linked Users */}
                <div className="bg-white border border-gray-200 rounded p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiLink className="w-4 h-4 text-teal-600" /> Linked Users ({request.certification.certifications?.length || 0})
                  </h3>
                  {request.certification.certifications && request.certification.certifications.length > 0 ? (
                    <div className="space-y-2">
                      {request.certification.certifications.map(ul => ul.user && (
                        <div key={ul.id} className="flex items-center gap-2 p-2 bg-teal-50 rounded text-sm">
                          <FiUser className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <Link to={`/team/${ul.user.id}`} className="text-teal-800 hover:underline font-medium">
                            {ul.user.first_name} {ul.user.last_name}
                          </Link>
                          {ul.user.phone_number && <span className="text-xs text-gray-500 ml-auto">{ul.user.phone_number}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-400">
                      <FiUserCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">
                        {request.user_id && request.linked_user
                          ? `This request was raised for ${request.linked_user.first_name} ${request.linked_user.last_name}.`
                          : "No users linked yet"}
                      </p>
                      {request.status === "completed" && !request.user_id && canLinkUser && (
                        <button onClick={() => { setUsers([]); setLinkUserId(""); setShowLinkUser(true) }} className="mt-3 text-xs px-3 py-1.5 bg-blue-800 text-white rounded hover:bg-blue-900">Link User</button>
                      )}
                    </div>
                  )}
                </div>
                {certDocuments.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FiFileText className="w-4 h-4 text-blue-600" /> Deduction Documents ({certDocuments.length})
                    </h3>
                    <div className="space-y-2">
                      {certDocuments.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded border border-gray-100">
                          <div>
                            <p className="font-medium text-gray-800">{doc.document_name}</p>
                            <p className="text-xs text-gray-500 capitalize mt-0.5">{doc.document_type.replace(/_/g, ' ')}</p>
                          </div>
                          <a href={doc.document_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs flex-shrink-0 ml-3">
                            <FiDownload className="w-3 h-3" /> View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded p-8 text-center">
                <FiAward className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 text-sm mb-4">No certification added yet</p>
                {request.status === "completed" && canAddCertification && (
                  <button onClick={() => setShowAddCert(true)} className="text-sm px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900">Add Certification</button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "payments" && (
          <div className="space-y-3">
            {request.payment_records && request.payment_records.length > 0 ? (
              request.payment_records.map(pr => pr.training_payment && (
                <div key={pr.id} className="bg-white border border-gray-200 rounded p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800 capitalize">{pr.training_payment.payment_type?.replace(/_/g, " ")}</p>
                      {pr.training_payment.pi_number && <p className="text-xs text-gray-500 mt-0.5">PI: {pr.training_payment.pi_number}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${pr.training_payment.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {pr.training_payment.payment_status}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {pr.training_payment.pi_total_amount != null && (
                      <div className="flex justify-between col-span-2"><span className="text-gray-500">Total Amount</span><span className="font-medium">₹{Number(pr.training_payment.pi_total_amount).toLocaleString("en-IN")}</span></div>
                    )}
                    {pr.training_payment.per_person_amount != null && (
                      <div className="flex justify-between col-span-2"><span className="text-gray-500">Per Person</span><span className="font-medium">₹{Number(pr.training_payment.per_person_amount).toLocaleString("en-IN")}</span></div>
                    )}
                    {pr.training_payment.paid_at && (
                      <div className="flex justify-between col-span-2"><span className="text-gray-500">Paid At</span><span className="font-medium">{formatDate(pr.training_payment.paid_at)}</span></div>
                    )}
                    {pr.training_payment.creator && (
                      <div className="flex justify-between col-span-2"><span className="text-gray-500">Created By</span><span className="font-medium">{pr.training_payment.creator.first_name} {pr.training_payment.creator.last_name}</span></div>
                    )}
                  </div>
                  <Link to="/training/payments" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-800 hover:underline">
                    View Payment <FiExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              ))
            ) : (
              <div className="bg-white border border-gray-200 rounded p-8 text-center">
                <FaRupeeSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 text-sm">No payment records found</p>
              </div>
            )}
          </div>
        )}

        {/* ---- ACTION OFFCANVAS PANELS ---- */}

        {/* HR Verify Offcanvas */}
        {showHrAction && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowHrAction(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <div>
                  <h3 className="font-semibold text-gray-800">HR Verification</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{request.request_id} · {request.candidate_name}</p>
                </div>
                <button onClick={() => !actionLoading && setShowHrAction(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Training ID</span><span className="font-medium text-right">{request.request_id}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Candidate Name</span><span className="font-medium text-right">{request.candidate_name}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Course Requested</span><span className="font-medium text-right">{request.training_type?.training_name || "-"}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Requestor</span><span className="font-medium text-right">{request.creator ? `${request.creator.first_name} ${request.creator.last_name}` : "-"}</span></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Offered (optional)</label>
                  <input type="number" min={0} value={salaryOffered} onChange={e => setSalaryOffered(e.target.value)} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="e.g. 35000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone (optional)</label>
                  <input type="text" value={altPhone} onChange={e => setAltPhone(e.target.value)} maxLength={10} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="10-digit phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project <span className="text-red-500">*</span></label>
                  <SelectWithSearch2 options={hrProjects} value={hrProjectId} onChange={setHrProjectId} placeholder="Select project" minSearchLength={3} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HR Documents (optional)</label>
                  <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                    <FaUpload className="text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 truncate">{hrDocFiles.length > 0 ? `${hrDocFiles.length} file(s) selected` : 'Upload HR documents (all types)'}</span>
                    <input type="file" multiple className="hidden" disabled={actionLoading} onChange={e => setHrDocFiles(Array.from(e.target.files || []))} />
                  </label>
                  {hrDocFiles.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {hrDocFiles.map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          <span className="truncate">{f.name}</span>
                          <button type="button" onClick={() => setHrDocFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"><FaTimes /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks (required to reject)</label>
                  <textarea value={rejectionRemarks} onChange={e => setRejectionRemarks(e.target.value)} rows={2} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Reason for rejection..." />
                </div>
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={() => handleHrDecision(true)} disabled={actionLoading}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaCheck className="w-4 h-4" />} Approve
                </button>
                <button onClick={() => handleHrDecision(false)} disabled={actionLoading || !rejectionRemarks.trim() || !hrProjectId}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaBan className="w-4 h-4" />} Reject
                </button>
              </div>
            </div>
          </>
        )}

        {/* Training Verify Offcanvas */}
        {showTrainingVerify && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowTrainingVerify(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Training Verification</h3>
                <button onClick={() => !actionLoading && setShowTrainingVerify(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setTrainingVerifyAction("verify")}
                      className={`flex-1 py-2 text-sm rounded border ${trainingVerifyAction === "verify" ? "bg-blue-800 text-white border-blue-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                      Verify
                    </button>
                    <button type="button" onClick={() => setTrainingVerifyAction("reject")}
                      className={`flex-1 py-2 text-sm rounded border ${trainingVerifyAction === "reject" ? "bg-red-600 text-white border-red-600" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
                      Reject
                    </button>
                  </div>
                </div>
                {trainingVerifyAction === "verify" ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                      <textarea value={verifyRemarks} onChange={e => setVerifyRemarks(e.target.value)} rows={3} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Optional remarks..." />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Safety Documents (optional)</label>
                      <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                        <FaUpload className="text-gray-400" />
                        <span className="text-gray-600 truncate">{safetyDocFiles.length > 0 ? `${safetyDocFiles.length} file(s) selected` : 'Upload safety documents'}</span>
                        <input type="file" multiple className="hidden" disabled={actionLoading} onChange={e => setSafetyDocFiles(Array.from(e.target.files || []))} />
                      </label>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks *</label>
                    <textarea value={verifyRejectionRemarks} onChange={e => setVerifyRejectionRemarks(e.target.value)} rows={3} disabled={actionLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Reason for rejection..." />
                  </div>
                )}
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleTrainingVerify} disabled={actionLoading}
                  className={`flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm text-white rounded disabled:opacity-50 ${trainingVerifyAction === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-blue-800 hover:bg-blue-900"}`}>
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FiShield className="w-4 h-4" />}
                  {trainingVerifyAction === "reject" ? "Reject" : "Submit"}
                </button>
                <button onClick={() => { setShowTrainingVerify(false); setTrainingVerifyAction("verify"); setSafetyDocFiles([]); setVerifyRemarks(""); setVerifyRejectionRemarks("") }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Schedule Offcanvas */}
        {showSchedule && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowSchedule(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Schedule Training</h3>
                <button onClick={() => !actionLoading && setShowSchedule(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Training ID</span><span className="font-medium text-right">{request.request_id}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Candidate Name</span><span className="font-medium text-right">{request.candidate_name}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Course Requested</span><span className="font-medium text-right">{request.training_type?.training_name || "-"}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Requestor</span><span className="font-medium text-right">{request.creator ? `${request.creator.first_name} ${request.creator.last_name}` : "-"}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500 flex-shrink-0">Project</span><span className="font-medium text-right">{request.project?.project_name || "-"}</span></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Training Date *</label>
                  <input type="date" value={plannedDate} onChange={e => setPlannedDate(e.target.value)} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Training Location</label>
                  <input type="text" value={plannedLocation} onChange={e => setPlannedLocation(e.target.value)} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Training location" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Time</label>
                  <input type="time" value={reportingTime} onChange={e => setReportingTime(e.target.value)} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institute</label>
                  <SelectWithSearch2 options={institutes} value={selectedInstitute} onChange={setSelectedInstitute} placeholder="Select institute" minSearchLength={3} />
                </div>
                {selectedInstitute && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Training Center</label>
                    <SelectWithSearch2 options={instituteCenters} value={selectedCenter} onChange={setSelectedCenter} placeholder={instituteCenters.length === 0 ? "No centers available" : "Select center (optional)"} minSearchLength={0} />
                  </div>
                )}
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleSchedule} disabled={actionLoading || !plannedDate}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FiCalendar className="w-4 h-4" />} Schedule
                </button>
                <button onClick={() => setShowSchedule(false)} disabled={actionLoading} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Mark Complete Offcanvas */}
        {showComplete && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowComplete(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Training Action</h3>
                <button onClick={() => !actionLoading && setShowComplete(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                  <select value={completeAction} onChange={e => { setCompleteAction(e.target.value as any); setReschedPlannedDate(""); setReschedPlannedLocation(""); setReschedReportingTime(""); setReschedInstitute(null); setReschedCenter(null); setReschedInstituteCenters([]) }} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                    <option value="complete">Mark as Completed</option>
                    <option value="reschedule">Reschedule</option>
                    <option value="cancel_attended">Cancel (Attended)</option>
                    <option value="cancel_no_attended">Cancel (Not Attended)</option>
                  </select>
                </div>
                {/* Reschedule: show full schedule fields */}
                {completeAction === "reschedule" && (
                  <div className="space-y-3 border border-blue-100 rounded p-3 bg-blue-50">
                    <p className="text-xs font-medium text-blue-700">New Schedule Details</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Training Date</label>
                      <input type="date" value={reschedPlannedDate} onChange={e => setReschedPlannedDate(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Training Location</label>
                      <input type="text" value={reschedPlannedLocation} onChange={e => setReschedPlannedLocation(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Training location" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Time</label>
                      <input type="time" value={reschedReportingTime} onChange={e => setReschedReportingTime(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Institute</label>
                      <SelectWithSearch2 options={institutes} value={reschedInstitute} onChange={setReschedInstitute} placeholder="Select institute" minSearchLength={3} />
                    </div>
                    {reschedInstitute && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Training Center</label>
                        <SelectWithSearch2 options={reschedInstituteCenters} value={reschedCenter} onChange={setReschedCenter} placeholder={reschedInstituteCenters.length === 0 ? "No centers available" : "Select center (optional)"} minSearchLength={0} />
                      </div>
                    )}
                    {!reschedPlannedDate && <p className="text-xs text-gray-500">Leave date empty to return to training-verified for re-scheduling later.</p>}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                  <textarea value={completeRemarks} onChange={e => setCompleteRemarks(e.target.value)} rows={3} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" />
                </div>
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleComplete} disabled={actionLoading}
                  className={`flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm text-white rounded disabled:opacity-50 ${
                    completeAction === "cancel_attended" || completeAction === "cancel_no_attended" ? "bg-red-600 hover:bg-red-700" : "bg-blue-800 hover:bg-blue-900"
                  }`}>
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FiCheckCircle className="w-4 h-4" />}
                  {completeAction === "complete" ? "Mark Completed" : completeAction === "reschedule" ? "Reschedule" : "Cancel Training"}
                </button>
                <button onClick={() => { setShowComplete(false); setCompleteAction("complete"); setCompleteRemarks(""); setReschedPlannedDate(""); setReschedPlannedLocation(""); setReschedReportingTime(""); setReschedInstitute(null); setReschedCenter(null); setReschedInstituteCenters([]) }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Link User Offcanvas */}
        {showLinkUser && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowLinkUser(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Link to Permanent User</h3>
                <button onClick={() => !actionLoading && setShowLinkUser(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search User</label>
                  <input type="text" placeholder="Type name, employee code or phone..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-2"
                    onChange={e => debouncedSearchUsers(e.target.value)} />
                  {users.length > 0 && (
                    <select value={linkUserId} onChange={e => setLinkUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                      <option value="">Select user...</option>
                      {users.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permanent ID Card Front (optional)</label>
                  <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                    <FaUpload className="text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-600">{permanentIdCardFile ? permanentIdCardFile.name : 'Upload permanent ID card front'}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" disabled={actionLoading}
                      onChange={e => setPermanentIdCardFile(e.target.files?.[0] || null)} />
                  </label>
                  {permanentIdCardFile && (
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <span className="truncate">{permanentIdCardFile.name}</span>
                      <button type="button" onClick={() => setPermanentIdCardFile(null)} className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"><FaTimes /></button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permanent ID Card Back (optional)</label>
                  <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                    <FaUpload className="text-gray-400 flex-shrink-0" />
                    <span className="truncate text-gray-600">{permanentIdCardBackFile ? permanentIdCardBackFile.name : 'Upload permanent ID card back'}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" disabled={actionLoading}
                      onChange={e => setPermanentIdCardBackFile(e.target.files?.[0] || null)} />
                  </label>
                  {permanentIdCardBackFile && (
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <span className="truncate">{permanentIdCardBackFile.name}</span>
                      <button type="button" onClick={() => setPermanentIdCardBackFile(null)} className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"><FaTimes /></button>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleLinkUser} disabled={actionLoading || !linkUserId}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FiLink className="w-4 h-4" />} Link User
                </button>
                <button onClick={() => { setShowLinkUser(false); setPermanentIdCardFile(null); setPermanentIdCardBackFile(null); setUsers([]); setLinkUserId("") }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Revert Workflow Confirm Modal */}
        {showRevert && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => { if (!actionLoading) { setShowRevert(false); setRevertRemarks("") } }} />
            <div className="relative bg-white rounded shadow-xl w-full max-w-md p-5">
              <div className="flex items-center gap-2 mb-3">
                <FiRotateCcw className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-gray-800">Revert Workflow One Step</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                This moves the request from <span className="font-medium">{STATUS_LABELS[request.status] || request.status}</span> back to the previous stage.
                Stage-completion details for the current step will be cleared. This action is logged.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                value={revertRemarks}
                onChange={e => setRevertRemarks(e.target.value)}
                rows={3}
                disabled={actionLoading}
                placeholder="Why are you reverting this step?"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => { setShowRevert(false); setRevertRemarks("") }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
                <button onClick={handleRevert} disabled={actionLoading}
                  className="px-4 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1">
                  {actionLoading && <FaSpinner className="w-3.5 h-3.5 animate-spin" />} Revert
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deduction Update Offcanvas */}
        {showDeductionUpdate && request?.certification && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => { if (!actionLoading) { setShowDeductionUpdate(false); setDeductionDocFiles([]); setDeductionDocType("deduction_slip"); setDeductionDocDragging(false) } }} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Update Deduction</h3>
                <button onClick={() => { if (!actionLoading) { setShowDeductionUpdate(false); setDeductionDocFiles([]); setDeductionDocType("deduction_slip"); setDeductionDocDragging(false) } }} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {request.deduction_applicable && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm space-y-1">
                    <p className="font-medium text-orange-800">Request Deduction Details</p>
                    <p className="text-orange-700">Period: <span className="font-medium capitalize">{request.deduction_period?.replace(/_/g, " ") || "-"}</span></p>
                    {request.deduction_remarks && <p className="text-orange-700">Remarks: <span className="font-medium">{request.deduction_remarks}</span></p>}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Status</label>
                  <select value={deductionAction} onChange={e => setDeductionAction(e.target.value as any)} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                  <option value="deducted">Deduction Booked</option>
                  <option value="payment_received">Payment Received</option>
                  <option value="waived">Deduction to be Booked</option>
                  <option value="pending">Deduction Pending</option>
                </select>
              </div>
                {(deductionAction === "deducted" || deductionAction === "waived" || deductionAction === "pending") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Amount{deductionAction === "deducted" ? " *" : ""}</label>
                    <input type="number" value={deductionAmount} onChange={e => setDeductionAmount(e.target.value)} disabled={actionLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Amount" step="0.01" />
                  </div>
                )}
                {deductionAction === "deducted" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Date</label>
                      <input type="date" value={deductionDate} onChange={e => setDeductionDate(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                    </div>
                  </>
                )}
                {deductionAction === "payment_received" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                      <input type="number" value={paymentReceivedAmount} onChange={e => setPaymentReceivedAmount(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" placeholder="Amount" step="0.01" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                      <input type="date" value={paymentReceivedDate} onChange={e => setPaymentReceivedDate(e.target.value)} disabled={actionLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea value={deductionRemarks} onChange={e => setDeductionRemarks(e.target.value)} rows={2} disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Optional remarks..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={deductionDocType}
                    onChange={e => setDeductionDocType(e.target.value)}
                    disabled={actionLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white mb-2"
                  >
                    <option value="deduction_slip">Deduction Slip</option>
                    <option value="salary_slip">Salary Slip</option>
                    <option value="receipt">Receipt</option>
                    <option value="other">Other</option>
                  </select>
                  <div
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!actionLoading) setDeductionDocDragging(true) }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDeductionDocDragging(false) }}
                    onDrop={e => {
                      e.preventDefault(); e.stopPropagation(); setDeductionDocDragging(false)
                      if (!actionLoading) addDeductionDocFiles(e.dataTransfer.files)
                    }}
                    className={`relative border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${
                      actionLoading ? 'opacity-50 cursor-not-allowed border-gray-200' :
                      deductionDocDragging ? 'border-blue-500 bg-blue-50' :
                      'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
                    }`}
                  >
                    <input
                      ref={deductionDocInputRef}
                      type="file"
                      multiple
                      disabled={actionLoading}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                      onChange={e => {
                        addDeductionDocFiles(e.target.files || [])
                        e.currentTarget.value = ""
                      }}
                    />
                    <FaUpload className="w-5 h-5 text-gray-400 mx-auto mb-1 pointer-events-none" />
                    <p className="text-xs text-gray-500 pointer-events-none">
                      {deductionDocFiles.length > 0 ? `${deductionDocFiles.length} file(s) selected` : "Drag & drop or click to upload any file type"}
                    </p>
                  </div>
                  {deductionDocFiles.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {deductionDocFiles.map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          <span className="truncate">{f.name}</span>
                          <button type="button" onClick={e => { e.stopPropagation(); setDeductionDocFiles(prev => prev.filter((_, idx) => idx !== i)) }} className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"><FaTimes /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleDeductionUpdate} disabled={actionLoading || (deductionAction === "deducted" && !deductionAmount)}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaCheck className="w-4 h-4" />} Update Deduction
                </button>
                <button onClick={() => { setShowDeductionUpdate(false); setDeductionAmount(""); setDeductionDate(""); setDeductionRemarks(""); setDeductionDocFiles([]); setDeductionDocType("deduction_slip"); setDeductionDocDragging(false) }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

        {/* Comment Offcanvas */}
        {showComment && (
          <>
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !actionLoading && setShowComment(false)} />
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="font-semibold text-gray-800">Add Comment</h3>
                <button onClick={() => !actionLoading && setShowComment(false)} disabled={actionLoading} className="p-1.5 hover:bg-gray-100 rounded"><FaTimes className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <textarea value={comment} onChange={e => setComment(e.target.value)} rows={6} disabled={actionLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none" placeholder="Enter comment..." />
              </div>
              <div className="border-t p-5 flex gap-3">
                <button onClick={handleComment} disabled={actionLoading || !comment.trim()}
                  className="flex items-center justify-center gap-1.5 flex-1 px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50">
                  {actionLoading ? <FaSpinner className="animate-spin w-4 h-4" /> : <FiMessageSquare className="w-4 h-4" />} Add Comment
                </button>
                <button onClick={() => { setShowComment(false); setComment("") }} disabled={actionLoading}
                  className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 disabled:opacity-50">Cancel</button>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Step Details Offcanvas */}
      {stepDetailsOpen && selectedStep && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setStepDetailsOpen(false)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h3 className="font-semibold text-gray-800">{selectedStep.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedStep.description}</p>
              </div>
              <button onClick={() => setStepDetailsOpen(false)} className="p-1.5 hover:bg-gray-100 rounded">
                <FaTimes className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {stepLogs.length === 0 && stepDocs.length === 0 && stepDetailRows.length === 0 && selectedStepKey !== "certification_done" ? (
                <div className="text-center py-8 text-gray-400">
                  <FiClock className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No activity recorded for this step yet</p>
                </div>
              ) : (
                <>
                  {stepDetailRows.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Submitted Details</p>
                      <div className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
                        {stepDetailRows.map((row) => (
                          <div key={row.label} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                            <span className="text-gray-500">{row.label}</span>
                            <span className="text-right font-medium text-gray-800 break-words">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {stepLogs.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Activity</p>
                      <div className="space-y-3">
                        {stepLogs.map(log => (
                          <div key={log.id} className="bg-gray-50 rounded p-3 text-sm space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">{ACTION_LABELS[log.action_type] || log.action_type}</span>
                              <span className="text-xs text-gray-400">{formatDate(log.performed_at)}</span>
                            </div>
                            {log.performer && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <FiUser className="w-3 h-3" />
                                {log.performer.first_name} {log.performer.last_name}
                              </p>
                            )}
                            {log.remarks && <p className="text-xs text-gray-600 italic">"{log.remarks}"</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {stepDocs.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Documents</p>
                      <div className="space-y-2">
                        {stepDocs.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <span className="capitalize text-gray-700">{doc.document_type.replace(/_/g, " ")}</span>
                            <a href={doc.document_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-800 hover:underline text-xs">
                              View <FiExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedStepKey === "certification_done" && request.certification && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Certification</p>
                      <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1">
                        <div className="flex justify-between"><span className="text-green-700">Cert No.</span><span className="font-mono font-medium">{request.certification.certificate_number}</span></div>
                        <div className="flex justify-between"><span className="text-green-700">Date</span><span>{formatDateOnly(request.certification.training_date)}</span></div>
                        <div className="flex justify-between"><span className="text-green-700">Expiry</span><span>{formatDateOnly(request.certification.expiry_date)}</span></div>
                        {request.certification.creator && (
                          <div className="flex justify-between"><span className="text-green-700">Added By</span><span>{request.certification.creator.first_name} {request.certification.creator.last_name}</span></div>
                        )}
                      </div>
                      {(request.certification.card_front_url || request.certification.card_back_url) && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {request.certification.card_front_url && (
                            <a href={request.certification.card_front_url} target="_blank" rel="noreferrer">
                              <img src={request.certification.card_front_url} alt="Card Front" className="w-full rounded border border-gray-200 object-contain max-h-28 bg-gray-50 hover:opacity-90" />
                              <p className="text-xs text-center text-gray-500 mt-1">Front</p>
                            </a>
                          )}
                          {request.certification.card_back_url && (
                            <a href={request.certification.card_back_url} target="_blank" rel="noreferrer">
                              <img src={request.certification.card_back_url} alt="Card Back" className="w-full rounded border border-gray-200 object-contain max-h-28 bg-gray-50 hover:opacity-90" />
                              <p className="text-xs text-center text-gray-500 mt-1">Back</p>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedStepKey === "certification_done" && !request.certification && (
                    <div className="text-center py-6 text-gray-400">
                      <FiAward className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Certification has not been added yet</p>
                    </div>
                  )}

                  {selectedStepKey === "user_linked" && request.certification?.certifications && request.certification.certifications.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Linked Users</p>
                      <div className="space-y-2">
                        {request.certification.certifications.map(ul => ul.user && (
                          <div key={ul.id} className="flex items-center gap-2 p-2 bg-teal-50 rounded text-sm">
                            <FiUser className="w-4 h-4 text-teal-600 flex-shrink-0" />
                            <Link to={`/team/${ul.user.id}`} className="text-teal-800 hover:underline font-medium">{ul.user.first_name} {ul.user.last_name}</Link>
                            {ul.user.phone_number && <span className="text-xs text-gray-500 ml-auto">{ul.user.phone_number}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedStepKey === "user_linked" && linkedUserDocuments.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Linked Documents</p>
                      <div className="space-y-2">
                        {linkedUserDocuments.map((doc) => (
                          <div key={doc.label} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <span className="text-gray-700">{doc.label}</span>
                            <a href={doc.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-800 hover:underline text-xs">
                              View <FiExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="border-t p-4">
              <button onClick={() => setStepDetailsOpen(false)} className="w-full px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700">Close</button>
            </div>
          </div>
        </>
      )}

      {/* Add Certification Offcanvas */}
      <AddCertificationOffcanvas
        isOpen={showAddCert}
        onClose={() => setShowAddCert(false)}
        onSubmit={handleAddCertification}
        trainingTypes={trainingTypes}
        institutes={certInstitutes}
        trainingRequestOptions={request ? [{ id: request.id, value: request.id, label: `${request.request_id} — ${request.candidate_name}` }] : []}
        loading={actionLoading}
      />
    </>
  )
}
